import { useQuery } from '@tanstack/react-query';
import { HubApi } from '@src/services/hub/hub-api';
import { LutienApi } from '@src/services/lutien/lutien-api';

const STALE_TIME_MS = 5 * 60 * 1000;

const hubApi = new HubApi();
const lutienApi = new LutienApi();

export interface ReferenceOption {
  id: string;
  label: string;
  detail?: string;
}

export const useOrganizationWorkflows = (organizationId?: string) =>
  useQuery({
    queryKey: ['hub-organization-workflows', organizationId],
    queryFn: async (): Promise<ReferenceOption[]> => {
      const workflows = await hubApi.v1.crm.workflows.get({ params: { organizationId: organizationId as string } });
      return workflows
        .map((workflow) => ({ id: workflow.id, label: workflow.title }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    },
    enabled: Boolean(organizationId),
    staleTime: STALE_TIME_MS,
    retry: false,
  });

export const useWorkflowPhases = (organizationId?: string, workflowId?: string) => {
  const workflows = useOrganizationWorkflows(organizationId);
  const isKnownWorkflow = Boolean(workflowId && workflows.data?.some((workflow) => workflow.id === workflowId));
  const phases = useQuery({
    queryKey: ['hub-workflow-phases', workflowId],
    queryFn: async (): Promise<ReferenceOption[]> => {
      const workflow = await hubApi.v1.crm.workflows.getById({ id: workflowId as string });
      return [...workflow.phases]
        .sort((a, b) => a.index - b.index)
        .map((phase, position) => ({ id: phase.id, label: phase.name, detail: `fase ${position + 1}` }));
    },
    enabled: isKnownWorkflow,
    staleTime: STALE_TIME_MS,
    retry: false,
  });
  return { workflows, phases, isKnownWorkflow };
};

export const useOrganizationOptions = (isEnabled: boolean) =>
  useQuery({
    queryKey: ['lutien-organization-options'],
    queryFn: async (): Promise<ReferenceOption[]> => {
      const organizations = await lutienApi.v1.organizations.get({
        settings: { maxRetries: 0, retryDelay: 0, delayType: 'linear' },
      });
      return organizations
        .map((organization) => ({
          id: organization.id,
          label: organization.fantasyName || organization.corporateName || organization.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    },
    enabled: isEnabled,
    staleTime: STALE_TIME_MS,
    retry: false,
  });
